import {ResourcesLoader} from '@fantasmagames/core/src/loader/ResourcesLoader';
import {injectable} from 'inversify';
import {MicrogamingPreloaderV2} from '@fantasmagames/core/src/loader/MicrogamingPreloaderV2';
import {BaseMicrogamingPreloaderView} from '@fantasmagames/core/src/loader/BaseMicorgamingPreloaderView';

@injectable()
export class GameResourceLoader extends ResourcesLoader {
	protected addLocaleLogo(): void {
		if (this._locale.indexOf('zh') > -1) {
			if (this._locale.indexOf('cn') > -1) {
				this._resources.push('LOGO_ZH_CN.png');
				this._resources.push('LOGO_ZH_CN_STARTSCREEN.png');
			} else {
				this._resources.push('LOGO_ZH_TW.png');
				this._resources.push('LOGO_ZH_TW_STARTSCREEN.png');
			}
		} else {
			this._resources.push('LOGOTYPE.png');
		}
	}

	protected removeUnsupportedLocales(): void {
		// For debugging and developing.
		if (!this.shouldRemoveUnsupportedLocales) {
			return;
		}

		this._translationResources = this._translationResources.filter((item) => {
			const lang = item.split('/languages/')[1].split('.')[0];
			if (this._supportedLocales.indexOf(lang) > -1) {
				return true;
			}
		});

		this._localeTexts = this._localeTexts.filter((item) => {
			const lang = item.split('LOCALE_TEXTS/')[1].split('.')[0];
			if (this._supportedLocales.indexOf(lang) > -1) {
				return true;
			}
		});
	}

	protected addPreloader(): void {
		this._preloader = new MicrogamingPreloaderV2(this._getUrlPath, this._device, BaseMicrogamingPreloaderView);
		this._preloader.createLoadingScreen();
	}

	public getPaytableHTML(): string {
		if (!this.shouldRemoveUnsupportedLocales || this._supportedLocales.indexOf[this.getLocale()] > -1) {
			return super.getPaytableHTML();
		}

		return this._helpFilesMap[`html/paytable/paytable_en.htm`];
	}

	public getGameRulesHTML(): string {
		if (!this.shouldRemoveUnsupportedLocales || this._supportedLocales.indexOf[this.getLocale()] > -1) {
			return super.getGameRulesHTML();
		}

		return this._helpFilesMap[`html/gamerules/gamerules_en.htm`];
	}

	protected initialize(): void {
		this._loadingLandscapeAndPortraitResources = false;
		this.shouldRemoveUnsupportedLocales = false;

		this._mobileSoundResourcesFiles = [
			'resources/mobileSounds/all.mp3',

			// music
			'resources/sounds/music/music_main_1.mp3',
			'resources/sounds/music/music_main_2.mp3',
			'resources/sounds/music/music_main_3.mp3',
			'resources/sounds/music/music_fs.mp3',
			'resources/sounds/music/win_bigwin_loop.mp3',
		];

		this._soundResources = [
			'resources/sounds/atn_respin.mp3',
			'resources/sounds/atn_scatter_1.mp3',
			'resources/sounds/atn_scatter_2.mp3',
			'resources/sounds/atn_scatter_3.mp3',
			'resources/sounds/btn_general.mp3',
			'resources/sounds/btn_spin_start.mp3',
			'resources/sounds/btn_spin_stop.mp3',
			'resources/sounds/mult_collect_fly.mp3',
			'resources/sounds/mult_collect_hit.mp3',
			'resources/sounds/mult_collect_indicator.mp3',
			'resources/sounds/mult_update.mp3',
			'resources/sounds/mult_update_fs.mp3',
			'resources/sounds/mult_wildbreak.mp3',
			'resources/sounds/mult_win.mp3',
			'resources/sounds/music/music_main_1.mp3',
			'resources/sounds/music/music_main_2.mp3',
			'resources/sounds/music/music_main_3.mp3',
			'resources/sounds/music/music_fs.mp3',
			'resources/sounds/near_fs.mp3',
			'resources/sounds/reels_fall_in.mp3',
			'resources/sounds/reels_fall_in_3sec.mp3',
			'resources/sounds/reels_fall_out.mp3',
			'resources/sounds/reels_remove.mp3',
			'resources/sounds/rsp_activate.mp3',
			'resources/sounds/rsp_hit01.mp3',
			'resources/sounds/rsp_hit02.mp3',
			'resources/sounds/rsp_hit03.mp3',
			'resources/sounds/rsp_move.mp3',
			'resources/sounds/rsp_end.mp3',
			'resources/sounds/trans_game_intro.mp3',
			'resources/sounds/trans_fs_intro.mp3',
			'resources/sounds/trans_fs_outro.mp3',
			'resources/sounds/win_bigwin_buildup.mp3',
			'resources/sounds/win_bigwin_end.mp3',
			'resources/sounds/music/win_bigwin_loop.mp3',
			'resources/sounds/win_bigwin_threshold.mp3',
			'resources/sounds/win_countup.mp3',
			'resources/sounds/win_excellent.mp3',
			'resources/sounds/win_indicate_lr.mp3',
			'resources/sounds/win_indicate_rl.mp3',
			'resources/sounds/win_nice.mp3',
			'resources/sounds/win_normal.mp3',
			'resources/sounds/win_retrigger.mp3',
			];

		this._configFiles = ['resources/config.json'];

		this._spineAtlasFile = 'SPINE_TEXTURES.atlas';
		this._atlasImages = ['SPINE_TEXTURES.png', 'SPINE_TEXTURES2.png', 'SPINE_TEXTURES3.png', 'SPINE_TEXTURES4.png', 'SPINE_TEXTURES5.png', 'SPINE_TEXTURES6.png', 'SPINE_TEXTURES7.png', 'SPINE_TEXTURES8.png'];
		if (this._device.isMobile) {
			this._atlasImages = ['SPINE_TEXTURES.png', 'SPINE_TEXTURES2.png', 'SPINE_TEXTURES3.png', 'SPINE_TEXTURES4.png'];
		}

		this._helpStyles = ['HTML/STYLE.css'];

		this._helpFiles = ['HTML/PAYTABLE/Paytable_en.htm', 'HTML/GAMERULES/Gamerules_en.htm']; // Load the english ones.

		this._mobileSoundDataFile = 'resources/mobileSounds/all.json';

		this._particleResources = [
			// particles
			'resources/particles/NICE_WIN_COINS.json',
			'resources/particles/EXCELLENT_WIN_COINS.json',
			'resources/particles/BIG_WIN_COINS.json',
			'resources/particles/SUPER_WIN_COINS.json',
			'resources/particles/MEGA_WIN_COINS.json',
			'resources/particles/EPIC_WIN_COINS.json',
			'resources/particles/EPIC_WIN_WAVE_COINS.json',

			`resources/particles/${this._device.isMobile ? 'MOBILE' : 'DESKTOP'}/COIN.json`,
		];

		this._translationResources = [
			'resources/languages/en.json',
			'resources/languages/bg.json',
			'resources/languages/cs.json',
			'resources/languages/da.json',
			'resources/languages/de.json',
			'resources/languages/el.json',
			'resources/languages/es.json',
			'resources/languages/es-419.json',
			'resources/languages/et.json',
			'resources/languages/fi.json',
			'resources/languages/fr.json',
			'resources/languages/hr.json',
			'resources/languages/hu.json',
			'resources/languages/id.json',
			'resources/languages/is.json',
			'resources/languages/it.json',
			'resources/languages/ja.json',
			'resources/languages/ko.json',
			'resources/languages/lt.json',
			'resources/languages/lv.json',
			'resources/languages/nl.json',
			'resources/languages/no.json',
			'resources/languages/pl.json',
			'resources/languages/pt.json',
			'resources/languages/pt-br.json',
			'resources/languages/ro.json',
			'resources/languages/ru.json',
			'resources/languages/sk.json',
			'resources/languages/sl.json',
			'resources/languages/sv.json',
			'resources/languages/sr.json',
			'resources/languages/th.json',
			'resources/languages/tr.json',
			'resources/languages/vi.json',
			'resources/languages/zh.json',
			'resources/languages/zh-cn.json',
			'resources/languages/zh-hant.json',
			'resources/languages/zh-tw.json',
		];

		this._spineResources = [
			'ACCEPT_BUTTON.json',
			'ARROW_BUTTON.json',
			'AUTOPLAY_BUTTON.json',
			'AUTOPLAY_MENU.json',

			'BIG_WINS.json',
			'BIG_WINS_GLOW.json',
			'BIG_WINS_EFFECT_01.json',
			'BIG_WINS_EFFECT_02.json',
			'BIG_WINS_EFFECT_03.json',

			'BONUS_SCATTER.json',

			'BOX_MENU.json',

			'DECLINE_BUTTON.json',

			'ERROR_MESSAGE.png',

			'FEATURE_SCREENS.json',

			'FREESPIN_BUTTON.json',
			'FREESPIN_STARTSCREEN.json',
			'FREESPIN_COMPLETE_SCREEN.json',
			'FREESPIN_FEATURE_SCREENS.json',

			'GENERIC_BUTTON.json',

			'SETTINGS_MENU.json',
			'GUI_INFO.json',

			'LOGOTYPE.json',

			'PLAY_BUTTON.json',
			'BET_BUTTON.json',
			'MENU_BUTTON.json',
			'MAIN_MENU.json',
			'SETTINGS_CONFIG_BUTTON.json',

			'STARTSCREEN.json',

			'SPIN_BUTTON_NOGLOW.json',
			'SPIN_BUTTON_GLOW.json',
			'SPIN_BUTTON_NOGLOW_AUTOPLAY.json',

			'INFO_MESSAGE.png',

			'START_BUTTON.json',
			'WIN_NUMBERS.json',

			'RESPIN.json',
			'WIN_SCREEN.json',
			'BASEGAME_BACKGROUND.json',
			'FREESPINS_BACKGROUND.json',

			'LAYOUT_TEMPLATE.json',
			'LAYOUT_TEMPLATE_FREESPIN.json',

			'TOP_REEL.json',
			'TOP_REEL_EFFECT.json',
			'ROW_DIVIDER.json',
			'ROW_DIVIDER_EFFECT.json',

			'SYMBOL_REMOVE.json',

			'TOP_BONUS.json',
			'TOP_RESPIN.json',
			'TOP_WILD.json',

			'WILD_MULTIPLIER_METER.json',
			'WILD_MULTIPLIER.json',
			'WILD.json',
			'WILD_SOUL.json',

			'INITIAL_WIN.json',
			'INITIAL_WIN_COVER.json',

			'TEXT_ANNOUNCEMENT.json',

			'WILD_MULTIPLIER_EFFECT.json',
		];

		this._resources = [
			'LAYOUT_TEMPLATE.json',
			'LAYOUT_TEMPLATE_FREESPIN.json',

			'TILT_ICON.png',
			'TOUCH_ICON.png',
			'INFO_MESSAGE.png',
			'ERROR_MESSAGE.png',

			'SYMBOLS.json',
			'HUD.json',

			'NUMBERS.json',

			// Added for safety check.
			'LOCALE_TEXTS/en.json',
		];

		this.addLocaleLogo();

		this._resources = this._resources.concat(this._spineResources);

		////////////////////////////////////////////////////////////////////
		// Device specific resources.
		////////////////////////////////////////////////////////////////////
		if (!this._device.isMobile) {
			////////////////////////////////////////////////////////////////////
			// PC
			////////////////////////////////////////////////////////////////////
			const pcResources: string[] = [];

			this._resources = this._resources.concat(pcResources);
		} else {
			////////////////////////////////////////////////////////////////////
			// Mobile
			////////////////////////////////////////////////////////////////////
			const mobileResources: string[] = ['TOUCH_ICON.png'];
			this._resources = this._resources.concat(mobileResources);
		}

		this._localeTexts = [
			'LOCALE_TEXTS/en.json',
			'LOCALE_TEXTS/bg.json',
			'LOCALE_TEXTS/cs.json',
			'LOCALE_TEXTS/da.json',
			'LOCALE_TEXTS/de.json',
			'LOCALE_TEXTS/el.json',
			'LOCALE_TEXTS/es.json',
			'LOCALE_TEXTS/es-419.json',
			'LOCALE_TEXTS/et.json',
			'LOCALE_TEXTS/fi.json',
			'LOCALE_TEXTS/fr.json',
			'LOCALE_TEXTS/hr.json',
			'LOCALE_TEXTS/hu.json',
			'LOCALE_TEXTS/id.json',
			'LOCALE_TEXTS/is.json',
			'LOCALE_TEXTS/it.json',
			'LOCALE_TEXTS/ja.json',
			'LOCALE_TEXTS/ko.json',
			'LOCALE_TEXTS/lt.json',
			'LOCALE_TEXTS/lv.json',
			'LOCALE_TEXTS/nl.json',
			'LOCALE_TEXTS/no.json',
			'LOCALE_TEXTS/pt.json',
			'LOCALE_TEXTS/pl.json',
			'LOCALE_TEXTS/pt-br.json',
			'LOCALE_TEXTS/ro.json',
			'LOCALE_TEXTS/ru.json',
			'LOCALE_TEXTS/sk.json',
			'LOCALE_TEXTS/sl.json',
			'LOCALE_TEXTS/sv.json',
			'LOCALE_TEXTS/sr.json',
			'LOCALE_TEXTS/th.json',
			'LOCALE_TEXTS/tr.json',
			'LOCALE_TEXTS/vi.json',
			'LOCALE_TEXTS/zh.json',
			'LOCALE_TEXTS/zh-cn.json',
			'LOCALE_TEXTS/zh-hant.json',
			'LOCALE_TEXTS/zh-tw.json',
		];

		this._currencyResources = [
			'CURRENCIES/AED.json',
			'CURRENCIES/ALL.json',
			'CURRENCIES/AMD.json',
			'CURRENCIES/AOA.json',
			'CURRENCIES/ARS.json',
			'CURRENCIES/ATS.json',
			'CURRENCIES/AUD.json',
			'CURRENCIES/BAM.json',
			'CURRENCIES/BEF.json',
			'CURRENCIES/BGN.json',
			'CURRENCIES/BOB.json',
			'CURRENCIES/BRL.json',
			'CURRENCIES/CAD.json',
			'CURRENCIES/CDF.json',
			'CURRENCIES/CHF.json',
			'CURRENCIES/CNY.json',
			'CURRENCIES/COP.json',
			'CURRENCIES/CRC.json',
			'CURRENCIES/CVE.json',
			'CURRENCIES/CZK.json',
			'CURRENCIES/DEM.json',
			'CURRENCIES/DJF.json',
			'CURRENCIES/DKK.json',
			'CURRENCIES/DOP.json',
			'CURRENCIES/DZD.json',
			'CURRENCIES/EEK.json',
			'CURRENCIES/EGP.json',
			'CURRENCIES/ERN.json',
			'CURRENCIES/ESP.json',
			'CURRENCIES/ETB.json',
			'CURRENCIES/EUR.json',
			'CURRENCIES/FIM.json',
			'CURRENCIES/FRF.json',
			'CURRENCIES/GBP.json',
			'CURRENCIES/GEL.json',
			'CURRENCIES/GHC.json',
			'CURRENCIES/GHS.json',
			'CURRENCIES/GMD.json',
			'CURRENCIES/GNF.json',
			'CURRENCIES/GRD.json',
			'CURRENCIES/HKD.json',
			'CURRENCIES/HNL.json',
			'CURRENCIES/HRK.json',
			'CURRENCIES/HUF.json',
			'CURRENCIES/IDR.json',
			'CURRENCIES/IEP.json',
			'CURRENCIES/ILS.json',
			'CURRENCIES/INR.json',
			'CURRENCIES/ITL.json',
			'CURRENCIES/JPY.json',
			'CURRENCIES/KES.json',
			'CURRENCIES/KGS.json',
			'CURRENCIES/KHR.json',
			'CURRENCIES/KMF.json',
			'CURRENCIES/KRW.json',
			'CURRENCIES/KZT.json',
			'CURRENCIES/LAK.json',
			'CURRENCIES/LRD.json',
			'CURRENCIES/LSL.json',
			'CURRENCIES/LTL.json',
			'CURRENCIES/LVL.json',
			'CURRENCIES/LYD.json',
			'CURRENCIES/MAD.json',
			'CURRENCIES/MDL.json',
			'CURRENCIES/MMK.json',
			'CURRENCIES/MOP.json',
			'CURRENCIES/MRO.json',
			'CURRENCIES/MUR.json',
			'CURRENCIES/MWK.json',
			'CURRENCIES/MXN.json',
			'CURRENCIES/MYR.json',
			'CURRENCIES/MZN.json',
			'CURRENCIES/NAD.json',
			'CURRENCIES/NGN.json',
			'CURRENCIES/NLG.json',
			'CURRENCIES/NOK.json',
			'CURRENCIES/NZD.json',
			'CURRENCIES/PEN.json',
			'CURRENCIES/PHP.json',
			'CURRENCIES/PKR.json',
			'CURRENCIES/PLN.json',
			'CURRENCIES/PTE.json',
			'CURRENCIES/PYG.json',
			'CURRENCIES/RDS.json',
			'CURRENCIES/RON.json',
			'CURRENCIES/RUB.json',
			'CURRENCIES/RWF.json',
			'CURRENCIES/SCR.json',
			'CURRENCIES/SDG.json',
			'CURRENCIES/SEK.json',
			'CURRENCIES/SGD.json',
			'CURRENCIES/SIK.json',
			'CURRENCIES/SKK.json',
			'CURRENCIES/SZL.json',
			'CURRENCIES/THB.json',
			'CURRENCIES/TRL.json',
			'CURRENCIES/TRY.json',
			'CURRENCIES/TWD.json',
			'CURRENCIES/TZH.json',
			'CURRENCIES/UAH.json',
			'CURRENCIES/UGX.json',
			'CURRENCIES/USD.json',
			'CURRENCIES/UYU.json',
			'CURRENCIES/VEF.json',
			'CURRENCIES/VND.json',
			'CURRENCIES/XAF.json',
			'CURRENCIES/XCD.json',
			'CURRENCIES/XOF.json',
			'CURRENCIES/ZAR.json',
			'CURRENCIES/ZMW.json',
		];
	}

	protected loadNextConfigFile(resource: any): void {
		if (this._configFilesLoaded >= 0) {
			if (resource.error === null && resource.url && resource.data !== null) {
				const name: string = resource.url;
				this._configFilesMap[name.substring(name.lastIndexOf('/') + 1, name.length)] = resource.data;
			}
		}

		this._configFilesLoaded++;
		if (this._configFilesLoaded >= this._configFilesExpectedToLoad) {
			this._configFilesLoadingCompletedCallback();

			return;
		}
		this._loader.reset();
		if (this._configFiles[this._configFilesLoaded].indexOf('cdnconfig.json') >= 0) {
			this._loader.add(`${location.protocol}//${location.host}/casino/cdnconfig.json`, {}, this.loadNextConfigFile.bind(this));
		} else {
			this._loader.add(this.getPathForResource(this._configFiles[this._configFilesLoaded]), {}, this.loadNextConfigFile.bind(this));
		}

		this._loader.load();
		this.updateProgress();
	}
}



// WEBPACK FOOTER //
// ./src/GameResourceLoader.ts